import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { auth, db } from "../../../firebase-config";

const DEFAULT_RSVP_SETTINGS = {
  collectEmail: true,
  collectPhone: false,
  collectPartySize: true,
  collectAdultsKids: true,
  collectKidsAges: false,
  collectGender: false,
  collectNotes: true,
  customQuestions: [],

  requireAudienceMatch: false,
  noMatchMessage: "",
  assignFormIdOnRsvp: "",
};

const determineAudienceType = (formData = {}) => {
  const raw = formData.targetAudience;

  const audiences = Array.isArray(raw)
    ? raw.map((a) => String(a).toLowerCase())
    : raw
    ? [String(raw).toLowerCase()]
    : [];

  const hasDonors = audiences.some((a) => a.includes("donor"));
  const hasFamiliesOrClients = audiences.some(
    (a) => a.includes("family") || a.includes("client")
  );

  if (hasDonors) return "donors";
  if (hasFamiliesOrClients) return "clients";
  return null;
};

const isTicketEvent = (formData = {}) => {
  const type = String(formData.eventType || "").toLowerCase();
  const primaryActionType = String(
    formData.micrositeConfig?.primaryActionType ||
      formData.primaryActionType ||
      ""
  ).toLowerCase();

  return type.includes("ticket") || primaryActionType === "ticket";
};

const RsvpSettingsStep = ({ formData = {}, setFormData }) => {
  const [formTemplates, setFormTemplates] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formsError, setFormsError] = useState("");

  const allowRsvps =
    formData.allowRsvps === false || formData.allowRsvps === "false"
      ? false
      : true;

  const settings = useMemo(
    () => ({
      ...DEFAULT_RSVP_SETTINGS,
      ...(formData.rsvpSettings || {}),
    }),
    [formData.rsvpSettings]
  );

  const audienceType = determineAudienceType(formData);
  const ticketEvent = isTicketEvent(formData);

  const showAudienceValidationSection =
    allowRsvps && !!audienceType && !ticketEvent;

  const showFormPicker =
    showAudienceValidationSection && audienceType === "clients";

  useEffect(() => {
    const loadPublishedForms = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn("No user yet, skipping form template fetch.");
      return;
    }

    const idToken = await user.getIdToken(true);
    const organizationId = sessionStorage.getItem("organizationId");

    if (!organizationId) {
      console.warn("Missing organizationId in sessionStorage.");
      return;
    }

    const res = await axios.post(
      "https://getformtemplates-774073159618.us-central1.run.app",
      {},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "organization-id": organizationId,
        },
      }
    );

    setFormTemplates(res.data.templates || []);
  } catch (error) {
    console.error("Failed to load form templates:", error);
    addToast("Failed to load form templates.", "error");
  }
};

    loadPublishedForms();
  }, [showFormPicker]);

  const updateSetting = (key) => (e) => {
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      rsvpSettings: {
        ...DEFAULT_RSVP_SETTINGS,
        ...(prev.rsvpSettings || {}),
        [key]: checked,
      },
    }));
  };

  const updateSettingValue = (key) => (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      rsvpSettings: {
        ...DEFAULT_RSVP_SETTINGS,
        ...(prev.rsvpSettings || {}),
        [key]: value,
      },
    }));
  };

  const labelSx = {
    opacity: 1,
    "& .MuiFormControlLabel-label": {
      opacity: 1,
      color: allowRsvps ? "text.primary" : "text.disabled",
      fontWeight: 500,
    },
  };

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ mb: 1, color: "text.primary", fontWeight: 600 }}
      >
        RSVP Settings
      </Typography>

      <Typography
        variant="body2"
        sx={{ mb: 3, color: "text.secondary", opacity: 1 }}
      >
        Choose what attendees must provide when RSVPing on the microsite.
      </Typography>

      {!allowRsvps && (
        <Typography
          variant="body2"
          sx={{ mb: 2, color: "warning.main", opacity: 1 }}
        >
          RSVPs are disabled for this event. Enable “Allow RSVPs” to customize
          RSVP fields.
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectEmail}
                onChange={updateSetting("collectEmail")}
                disabled={!allowRsvps}
              />
            }
            label="Email (recommended)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectPhone}
                onChange={updateSetting("collectPhone")}
                disabled={!allowRsvps}
              />
            }
            label="Phone Number"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectPartySize}
                onChange={updateSetting("collectPartySize")}
                disabled={!allowRsvps}
              />
            }
            label="Party Size"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectAdultsKids}
                onChange={updateSetting("collectAdultsKids")}
                disabled={!allowRsvps}
              />
            }
            label="Adults vs Kids Count"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectKidsAges}
                onChange={updateSetting("collectKidsAges")}
                disabled={!allowRsvps}
              />
            }
            label="Kids Ages / Age Groups"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectGender}
                onChange={updateSetting("collectGender")}
                disabled={!allowRsvps}
              />
            }
            label="Gender"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.collectNotes}
                onChange={updateSetting("collectNotes")}
                disabled={!allowRsvps}
              />
            }
            label="Notes / Special Requests"
          />
        </Grid>
      </Grid>

      {ticketEvent && (
        <Box mt={3}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Audience validation is skipped for ticketed events.
          </Typography>
        </Box>
      )}

      {showAudienceValidationSection && (
        <Box mt={4}>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, color: "text.primary", fontWeight: 600 }}
          >
            Audience Validation
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            This event targets{" "}
            <strong>
              {audienceType === "donors" ? "Donors" : "Families / Clients"}
            </strong>
            . You can require RSVPs to match a record in that collection before
            confirming.
          </Typography>

          <FormControlLabel
            sx={labelSx}
            control={
              <Checkbox
                checked={!!settings.requireAudienceMatch}
                onChange={updateSetting("requireAudienceMatch")}
                disabled={!allowRsvps}
              />
            }
            label="Require a matching donor/client record before confirming RSVP"
          />

          <Box mt={2}>
            <TextField
              fullWidth
              label="Message to show when no match is found (optional)"
              placeholder="Example: This RSVP is limited to verified donors. Please contact us if you believe this is a mistake."
              multiline
              minRows={3}
              value={settings.noMatchMessage || ""}
              onChange={updateSettingValue("noMatchMessage")}
              disabled={!allowRsvps}
            />
          </Box>

          {showFormPicker && (
            <Box mt={2}>
              <TextField
                select
                fullWidth
                label="Assign Published Form After RSVP (optional)"
                value={settings.assignFormIdOnRsvp || ""}
                onChange={updateSettingValue("assignFormIdOnRsvp")}
                disabled={!allowRsvps || loadingForms}
                helperText={
                  formsError
                    ? formsError
                    : "If selected, the matched client will be assigned this form after RSVP."
                }
              >
                <MenuItem value="">
                  {loadingForms ? "Loading forms..." : "No form assigned"}
                </MenuItem>

                {loadingForms && (
                  <MenuItem value="" disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      Loading published forms...
                    </Box>
                  </MenuItem>
                )}

                {!loadingForms &&
                  formTemplates.map((f) => (
                    <MenuItem key={f.formId || f.id} value={f.formId || f.id}>
                      {f.title || f.formName || f.name || "Untitled Form"}
                    </MenuItem>
                  ))}
              </TextField>

              {!loadingForms && !formsError && formTemplates.length === 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  No published forms found. Publish a form in Form Builder to select it here.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box mt={4}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: "text.primary", fontWeight: 600 }}
        >
          Custom Questions (coming next)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ opacity: 1 }}>
          We’ll add UI for custom RSVP questions right after this passes smoke
          testing.
        </Typography>
      </Box>
    </Box>
  );
};

export default RsvpSettingsStep;